/**
 * `shopify-install` — pure helpers for building the Shopify OAuth
 * install URL on the dashboard surface.
 *
 * Both surfaces that initiate a Shopify connect (the onboarding
 * `ShopifyConnectPanel` and the per-site `SettingsIntegrations` card)
 * must include a valid `*.myshopify.com` shop in the install URL.
 * The API endpoint at `/v1/oauth/shopify/install` rejects requests
 * missing the `shop` query param OR carrying anything that's not a
 * Shopify tenant subdomain with HTTP 400 `invalid_shop`
 * (services/api/src/connectors/shopify-oauth.ts:58–69).
 *
 * The shop validator here MIRRORS the server's `isValidShopDomain`
 * regex shape so client + server agree on what's acceptable. It is
 * intentionally strict: lowercased, single label before `.myshopify.`,
 * no path, no port, no trailing dot. We also accept `.myshopify.io`
 * (Shopify's preview / partner-dev TLD) so partner-stage stores can
 * connect — the API tolerates the same shape.
 *
 * NO caller may emit an install URL without a shop value:
 * `buildShopifyInstallUrl` requires `shop` as a parameter, and both
 * surfaces gate the Connect affordance on a shop that passes
 * {@link isValidShopInput} (for merchant-typed input) or
 * {@link deriveShopFromDomain} (when the site domain itself is the
 * tenant subdomain).
 *
 * Pure + dependency-free so it can be unit-tested in isolation.
 */

/**
 * Valid Shopify tenant subdomain. Must be `<store>.myshopify.com` or
 * `<store>.myshopify.io`, lowercase, with `<store>` starting alphanumeric
 * and continuing in `[a-z0-9-]`. Length capped at 60 chars before the
 * suffix (matches the server-side limit).
 */
const SHOP_DOMAIN_RE = /^[a-z0-9][a-z0-9-]{0,60}\.myshopify\.(com|io)$/;

/**
 * Normalize a domain: lowercase + strip a single trailing dot.
 * Whitespace is trimmed. Anything that contains a slash, scheme, or
 * port becomes the caller's problem — we leave it intact so the regex
 * below rejects it.
 */
function normalize(domain: string): string {
  const trimmed = domain.trim().toLowerCase();
  return trimmed.endsWith('.') ? trimmed.slice(0, -1) : trimmed;
}

/**
 * Derive a Shopify shop from a Site's `domain` field. Returns the
 * normalized shop if the site's domain IS a Shopify tenant subdomain;
 * returns `null` for custom-domain Shopify stores (e.g. `brand.com`)
 * where the caller MUST prompt the merchant for the underlying
 * `*.myshopify.com` value.
 */
export function deriveShopFromDomain(domain: string): string | null {
  if (typeof domain !== 'string') return null;
  const normalized = normalize(domain);
  if (normalized.length === 0 || normalized.length > 80) return null;
  return SHOP_DOMAIN_RE.test(normalized) ? normalized : null;
}

/**
 * Validate a merchant-entered shop value for the inline input. Same
 * shape as {@link deriveShopFromDomain} but exposed as a boolean for
 * the "Connect button enabled?" gate. Inputs are normalized
 * (lowercased, trailing dot stripped) BEFORE matching — an uppercase
 * "JohnnyVBT.MyShopify.com" entry is treated as the valid
 * "johnnyvbt.myshopify.com".
 */
export function isValidShopInput(shop: string): boolean {
  return deriveShopFromDomain(shop) !== null;
}

/**
 * Build the Shopify install URL with both required query params
 * URL-encoded. The `shop` argument is REQUIRED and must already have
 * been validated by the caller via {@link isValidShopInput} or
 * {@link deriveShopFromDomain}. The function does not silently drop an
 * invalid shop — it throws — so a caller cannot accidentally emit a
 * URL that the API will 400.
 */
export function buildShopifyInstallUrl(
  apiOrigin: string,
  site_id: string,
  shop: string,
): string {
  if (!isValidShopInput(shop)) {
    throw new Error(
      'buildShopifyInstallUrl: shop must be a valid *.myshopify.{com,io} domain',
    );
  }
  const normalized = normalize(shop);
  const u = new URL(`${apiOrigin}/v1/oauth/shopify/install`);
  u.searchParams.set('site_id', site_id);
  u.searchParams.set('shop', normalized);
  return u.toString();
}
